import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const NagTibba = () => {
  const tourData = {
    name: "Nag Tibba Trek",
    duration: "01 Nights 02 Days",
    price: "₹ 1,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935853/paosdyxmrcnhcpujwd85.webp",
    altitude: "9,915 ft",
    difficulty: "Easy",
    region: "Uttarakhand",
    highlights: ["Weekend trek", "Summit views", "Forest trails"],
    bestTime: "October to June",
    description: "The Nag Tibba Trek is a perfect weekend getaway trek in Uttarakhand that takes you to the highest peak in the Nag Tibba range. This easy trek offers stunning views of the Himalayas and is perfect for beginners looking for a short but rewarding mountain adventure."
  };

  return (
    <>
      <Helmet>
        <title>Nag Tibba Trek - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Nag Tibba Trek in Uttarakhand. Perfect weekend trek with summit views and forest trails." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Perfect weekend trek with summit views and forest trails
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek takes you to the highest peak in the Nag Tibba range, offering 
                    spectacular views of the Himalayas. The trek is perfect for beginners and 
                    weekend adventurers looking for a short but memorable mountain experience. 
                    The trail passes through beautiful forests and offers stunning summit views.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Perfect weekend trek</li>
                    <li>Summit views of Himalayas</li>
                    <li>Beautiful forest trails</li>
                    <li>Easy difficulty level</li>
                    <li>Great for beginners</li>
                    <li>Short duration</li>
                    <li>Photography opportunities</li>
                    <li>Mountain camping experience</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Travel Plan</h3>
                  <div className="day-item">
                    <h4>Day 01: Dehradun to Pantwari village and trek to Base Camp</h4>
                    <p><strong>Altitude:</strong> 2400 metres, 90 km drive and 4 km trek</p>
                    <ul>
                      <li>Pick up from Prince chowk near Dehradun Railway station at 6:00 AM.</li>
                      <li>A 90 km road ride to Pantwari village from Dehradun crossing the city of Mussoorie & Kempty Falls. Breakfast will be provided in the road side inns. The meal cost during the course of travel will be paid by the individual.</li>
                      <li>Arrival at Pantwari village, followed by lunch at the village. A 4 km trek gradual ascent to the campsite.</li>
                      <li>Evening snacks and refreshments in the evening.</li>
                      <li>Dinner at the campsite.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 02: Base camp to Nag Tibba and back to Pantwari to Dehradun</h4>
                    <p><strong>Altitude:</strong> 3022 metres, 12 km trek and 90 km drive</p>
                    <ul>
                      <li>Early excursion to Nag Tibba top at 5 a.m.</li>
                      <li>A 4 km ascend trek to summit of medium grade.</li>
                      <li>Carry 1 litre of water individually as the trail is deprived of water streams.</li>
                      <li>Reach at Nag Tibba top and stay there for a while.</li>
                      <li>Excursion back to Pantwari village from the top via camp1.</li>
                      <li>Lunch at Pantwari village followed by 90 km drive back to Dehradun.</li>
                      <li>Expected time of arrival in Dehradun 9.p.m.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Essential Gear</h4>
                      <ul>
                        <li>Backpack/Ruck Sack (at least 55 Litres) and a small day bag (upto 15 litres) for summit day</li>
                        <li>Poncho/Raincoat and Rain Cover for Ruck Sack</li>
                        <li>Comfortable Shoes with a good grip and ankle support. We recommend Quechua Forclaz 100 or similar shoe</li>
                        <li>Torch or Headlamp with extra pair of batteries</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>2 Quick Dry T-shirts (preferably Full Sleeves) and Track Pants</li>
                        <li>Warm Jacket preferably a down jacket or an equivalent heavy jacket to protect from cold at night</li>
                        <li>A pair of fleece jacket or fleece upper</li>
                        <li>Good Thermals for insulation at night</li>
                        <li>2 pair of warm socks and at least 2 pair of normal cotton socks</li>
                        <li>1 Pair of slippers/Sandals</li>
                        <li>Woolen Cap and Gloves for night</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-user"></i> Personal Items</h4>
                      <ul>
                        <li>Lip Balm and Cold Cream</li>
                        <li>Sun Cap or Sunscreen (SPF 30+)</li>
                        <li>Dark Sunglasses. People who wear spectacle may use photochromatic lenses in their spects</li>
                        <li>Toiletries - Tooth brush, Tooth paste, Liquid soap, Hand Sanitizers, Slippers, Light Towel, Toilet Roll (Avoid wet tissues as they are not eco friendly)</li>
                        <li>Water bottle 1 L or Hydration bag 2 L</li>
                        <li>Other personal accessories - Camera, Tripod, mobile charger, power banks(as there will be no electricity during the trek)</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-medkit"></i> Medical Kit</h4>
                      <ul>
                        <li>Personal Medical Kit: Crocin-Fever, Avomine-Motion Sickness, Avil 25mg - Allergies, Combiflam, Disprin-headache, Norflox TZ & Lomofen-diarrhea, digene-acidity, omez/rantadine antacid, crepe bandages, band aids, ORS, betadiene or antiseptic cream, moov spray/volini, cotton, gauze</li>
                      </ul>
                    </div>
                  </div>

                  <div className="important-notes">
                    <h4>Important Notes:</h4>
                    <ul>
                      <li>Being a high altitude trek, this trek is suitable for climbers with good physical condition and stamina.</li>
                      <li>This is a tentative itinerary which may change as per weather conditions</li>
                      <li>We expect you to carry your rucksacks, in case if you want them to be carried by mules back pack offloading charge up to Rs. 300/bag/day is applicable</li>
                      <li>In case of any natural calamity the company will not be able to refund the trip charges, for more details check our cancellation policy</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Pantwari to Pantwari</li>
                    <li>Veg Meals - Breakfast, Lunch, Evening Snacks and Dinner</li>
                    <li>Tents (Triple/Double Sharing), Sleeping Bags, Sleeping Matts</li>
                    <li>Gears: Micro Spikes and Gaiters as per snow conditions</li>
                    <li>Experienced and Qualified Guides</li>
                    <li>Friendly and supportive staff</li>
                    <li>Forest permits</li>
                    <li>Medical Amenities - First Aid Kits, Oxymeters, oxygen cylinders</li>
                    <li>Sanitised and Hygienic Camp sites and Hotels</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Anything not mentioned in inclusion list</li>
                    <li>Meals during Transportation</li>
                    <li>Any kind of personal Expenses</li>
                    <li>Unscheduled or extended stay due to road blocks, Landslides</li>
                    <li>Porter/mule charges (Personal), Back pack offloading Charges</li>
                    <li>Cost of Evacuation in case of emergency</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>9,915 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>16 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>October to June</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Starting From: ₹ 1,999/- per person</h4>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Main Pickup Point:</strong> Prince Chowk near Dehradun Railway Station</li>
                    <li><strong>Alternative Pickup:</strong> Hotel in Dehradun (if arranged in advance)</li>
                    <li><strong>Reporting Time:</strong> 6:00 AM on Day 1</li>
                    <li><strong>Drop Point:</strong> Dehradun Railway Station (Day 2, around 9:00 PM)</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Perfect weekend trek</li>
                    <li>Summit views of Himalayas</li>
                    <li>Beautiful forest trails</li>
                    <li>Easy difficulty level</li>
                    <li>Great for beginners</li>
                    <li>Short duration</li>
                    <li>Photography opportunities</li>
                    <li>Mountain camping experience</li>
                    <li>Experienced guides</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default NagTibba;
